import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Star, TrendingUp, Youtube, Gem, Sparkles, Leaf, Rocket, Clock, Flame } from 'lucide-react';
import { fetchDiscoveryBucket, fetchDiscoveryIndex, API } from '../api/client';

const bucketMeta = {
  editors_pick: { name: "Editor's Picks", icon: Star, blurb: "Our team's hand-picked, best-in-class AI resources you shouldn't miss." },
  trending_tiktok: { name: 'Trending on TikTok', icon: TrendingUp, blurb: 'AI tools blowing up on the For You page right now.' },
  trending_youtube: { name: 'Trending on YouTube', icon: Youtube, blurb: 'Tools & workflows every AI YouTuber is talking about.' },
  hidden_gem: { name: 'Hidden Gems', icon: Gem, blurb: "Under-the-radar tools that punch above their weight." },
  weird_ai: { name: 'Weird AI', icon: Sparkles, blurb: 'The strange, experimental, and wildly niche corners of AI.' },
  beginner_friendly: { name: 'Beginner Friendly', icon: Leaf, blurb: 'Great starting points if AI still feels overwhelming.' },
  rising_creator: { name: 'Rising Creators', icon: Rocket, blurb: 'New voices in the AI creator space worth following.' },
  new: { name: 'New & Notable', icon: Clock, blurb: 'The latest launches worth checking out.' },
  popular: { name: 'Most Popular', icon: Flame, blurb: 'The tools everyone in AI is actually using every day.' },
};

export function DiscoverIndex() {
  const [idx, setIdx] = useState({ buckets: [] });
  useEffect(() => {
    fetchDiscoveryIndex().then(setIdx).catch(() => setIdx({ buckets: [] }));
  }, []);

  return (
    <div className="page-container" data-testid="discover-index">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Discover</span>
      </div>
      <h1 className="page-title">Discover</h1>
      <p className="muted" style={{ marginBottom: 24 }}>
        Curated ways to explore the AI ecosystem — not just an alphabetical list.
      </p>
      <div className="discover-grid">
        {idx.buckets.map((b) => {
          const meta = bucketMeta[b.slug] || { name: b.name, icon: Star, blurb: '' };
          const Icon = meta.icon;
          return (
            <Link key={b.slug} to={`/discover/${b.slug}`} className="discover-card" data-testid={`discover-card-${b.slug}`}>
              <div className="discover-card-head">
                <Icon size={18} />
                <span className="discover-card-count">{b.count}</span>
              </div>
              <h3>{meta.name}</h3>
              <p>{meta.blurb}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function DiscoverPage() {
  const { slug } = useParams();
  const meta = bucketMeta[slug] || { name: slug, icon: Star, blurb: '' };
  const Icon = meta.icon;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchDiscoveryBucket(slug, 60)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="page-container" data-testid={`discover-page-${slug}`}>
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/discover">Discover</Link>
        <span>/</span>
        <span>{meta.name}</span>
      </div>
      <div className="discover-header">
        <div className="discover-header-icon"><Icon size={22} /></div>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>{meta.name}</h1>
          <p className="muted" style={{ marginTop: 4 }}>{meta.blurb}</p>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">No resources tagged with this yet — check back soon.</div>
      ) : (
        <div className="discover-list">
          {items.map((r) => {
            const to =
              r.resource_type === 'creator'
                ? `/creator/${r.slug}`
                : r.resource_type === 'company'
                ? `/company/${r.slug}`
                : `/tool/${r.slug}`;
            return (
              <div key={r.slug} className="discover-row" data-testid={`discover-row-${r.slug}`}>
                <Link to={to} className="discover-row-title">{r.name}</Link>
                <span className="discover-row-cat">{r.primary_category}</span>
                <span className="discover-row-type">{r.resource_type}</span>
                {r.badges?.slice(0, 3).map((b) => (
                  <span key={b} className="discover-row-badge">{b.replace(/_/g, ' ')}</span>
                ))}
                <a
                  href={`${API}/go/${r.slug}?ref=/discover/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="discover-row-visit"
                  aria-label="Visit"
                >
                  <ExternalLink size={13} />
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
